import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  TemplateRef,
  TrackByFunction,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { NzConfigService, WithConfig } from "ng-zorro-antd/core/config";
import { NzSafeAny } from "ng-zorro-antd/core/types";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { PaginationItemRenderContext } from "ng-zorro-antd/pagination";

import { UiTableDataService } from "@webapp/ui/table/services/table-data.service";
import { UiTableStyleService } from "@webapp/ui/table/services/table-style.service";
import { NzTableComponent } from "ng-zorro-antd/table";
import { UiTableInnerScrollComponent } from "@webapp/ui/table/components/table/table-inner-scroll/table-inner-scroll.component";
import { UiTableVirtualScrollDirective } from "@webapp/ui/table/directives/table/table-virtual-scroll.directive";
import { UiTableLayout, UiTablePaginationPosition, UiTablePaginationType, UiTableQueryParams, UiTableSize } from "@webapp/ui/table/table.models";
import { NzResizeObserver } from "ng-zorro-antd/cdk/resize-observer";
import { Directionality } from "@angular/cdk/bidi";

@Component({
  selector: "ui-table",
  exportAs: "uiTable",
  providers: [UiTableStyleService, UiTableDataService],
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: "table.component.html",
})
export class UiTableComponent<T> extends NzTableComponent<T> {
  @Input("uiTableLayout") public nzTableLayout: UiTableLayout = "auto";
  @Input("uiShowTotal") public nzShowTotal: TemplateRef<{ $implicit: number; range: [number, number] }> | null = null;
  @Input("uiItemRender") public nzItemRender: TemplateRef<PaginationItemRenderContext> | null = null;
  @Input("uiTitle") public nzTitle: string | TemplateRef<NzSafeAny> | null = null;
  @Input("uiFooter") public nzFooter: string | TemplateRef<NzSafeAny> | null = null;
  @Input("uiNoResult") public nzNoResult: string | TemplateRef<NzSafeAny> | undefined = undefined;
  @Input("uiPageSizeOptions") public nzPageSizeOptions = [10, 20, 30, 40, 50];
  @Input("uiVirtualItemSize") public nzVirtualItemSize = 0;
  @Input("uiVirtualMaxBufferPx") public nzVirtualMaxBufferPx = 200;
  @Input("uiVirtualMinBufferPx") public nzVirtualMinBufferPx = 100;
  @Input("uiVirtualForTrackBy") public nzVirtualForTrackBy: TrackByFunction<T> = (index) => index;
  @Input("uiLoadingDelay") public nzLoadingDelay = 0;
  @Input("uiPageIndex") public nzPageIndex = 1;
  @Input("uiPageSize") public nzPageSize = 10;
  @Input("uiTotal") public nzTotal = 0;
  @Input("uiWidthConfig") public nzWidthConfig: ReadonlyArray<string | null> = [];
  @Input("uiData") public nzData: readonly T[] = [];
  @Input("uiPaginationPosition") public nzPaginationPosition: UiTablePaginationPosition = "bottom";
  @Input("uiScroll") public nzScroll: { x?: string | null; y?: string | null } = { x: null, y: null };
  @Input("uiPaginationType") public nzPaginationType: UiTablePaginationType = "default";
  @Input("uiFrontPagination") @InputBoolean() public nzFrontPagination = true;
  @Input("uiTemplateMode") @InputBoolean() public nzTemplateMode = false;
  @Input("uiShowPagination") @InputBoolean() public nzShowPagination = true;
  @Input("uiLoading") @InputBoolean() public nzLoading = false;
  @Input("uiOuterBordered") @InputBoolean() public nzOuterBordered = false;
  @Input("uiLoadingIndicator") @WithConfig() public nzLoadingIndicator: TemplateRef<NzSafeAny> | null = null;
  @Input("uiBordered") @WithConfig() @InputBoolean() public nzBordered = false;
  @Input("uiSize") @WithConfig() public nzSize: UiTableSize = "default";
  @Input("uiShowSizeChanger") @WithConfig() @InputBoolean() public nzShowSizeChanger = false;
  @Input("uiHideOnSinglePage") @WithConfig() @InputBoolean() public nzHideOnSinglePage = false;
  @Input("uiShowQuickJumper") @WithConfig() @InputBoolean() public nzShowQuickJumper = false;
  @Input("uiSimple") @WithConfig() @InputBoolean() public nzSimple = false;
  @Output("uiPageSizeChange") public readonly nzPageSizeChange = new EventEmitter<number>();
  @Output("uiPageIndexChange") public readonly nzPageIndexChange = new EventEmitter<number>();
  @Output("uiQueryParams") public readonly nzQueryParams = new EventEmitter<UiTableQueryParams>();
  @Output("uiCurrentPageDataChange") public readonly nzCurrentPageDataChange = new EventEmitter<readonly T[]>();

  @ContentChild(UiTableVirtualScrollDirective, { static: false })
  public nzVirtualScrollDirective!: UiTableVirtualScrollDirective<T>;
  @ViewChild(UiTableInnerScrollComponent) public nzTableInnerScrollComponent!: UiTableInnerScrollComponent<T>;

  constructor(
    elementRef: ElementRef,
    nzResizeObserver: NzResizeObserver,
    nzConfigService: NzConfigService,
    cdr: ChangeDetectorRef,
    uiTableStyleService: UiTableStyleService,
    uiTableDataService: UiTableDataService<T>,
    @Optional() directionality: Directionality
  ) {
    // TODO: move to host after View Engine deprecation
    super(elementRef, nzResizeObserver, nzConfigService, cdr, uiTableStyleService, uiTableDataService, directionality);
  }
}
